import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-payment-modal',
  imports: [FormsModule, ReactiveFormsModule, CommonModule],
  templateUrl: './payment-modal.component.html',
  styleUrl: './payment-modal.component.scss',
})
export class PaymentModalComponent {
  @Input() totalAmount: number = 0;

  paymentForm: FormGroup;
  splitEnabled = false;
  selectedModes: string[] = [];

  constructor(public activeModal: NgbActiveModal, private fb: FormBuilder) {
    this.paymentForm = this.fb.group({
      cash: [0],
      card: [0],
      upi: [0, [this.twoDecimalValidator]],
    });

    // Log whenever form values change (only when split enabled)
    this.paymentForm.valueChanges.subscribe((values) => {

      if (!this.splitEnabled || this.selectedModes.length !== 2) return;

      const [first, second] = this.selectedModes;
      const firstVal = this.paymentForm.get(first)?.value || 0;
      const secondControl = this.paymentForm.get(second);

      const total = firstVal;
      let remaining = this.totalAmount - total;
      remaining = parseFloat(remaining.toFixed(2));

      console.log(
        `[Split Payment Update] Mode: ${first} = ${firstVal}, Remaining for ${second} = ${remaining}`
      );

      if (remaining >= 0 && secondControl?.value !== remaining) {
        secondControl?.setValue(remaining, { emitEvent: false });
        console.log(
          `[Auto Adjust] Updated ${second} to ${remaining.toFixed(2)}`
        );
      }
    });

    // Log and round UPI to 2 decimals
    this.paymentForm.get('upi')?.valueChanges.subscribe((val) => {
      if (val === null || val === '') return;

      const strVal = val.toString();

      // ✅ Allow only up to 2 decimal places
      if (/^\d+(\.\d{0,2})?$/.test(strVal)) {
        return; // valid input, do nothing
      }

      // If user tries more than 2 decimals, trim it
      const trimmed = strVal.match(/^\d+(\.\d{0,2})?/);
      if (trimmed) {
        this.paymentForm.get('upi')?.setValue(trimmed[0], { emitEvent: false });
      }
    });
  }

  twoDecimalValidator(control: any) {
    if (control.value === null || control.value === '') return null;

    const regex = /^\d+(\.\d{1,2})?$/;
    const isValid = regex.test(control.value);

    if (!isValid) {
      console.warn(
        `[Validation Failed] Field value '${control.value}' exceeds 2 decimals`
      );
    }

    return isValid ? null : { decimal: true };
  }

  isSelected(mode: string): boolean {
    return this.selectedModes.includes(mode);
  }

  onSplitToggle() {
    console.log(
      `[Split Toggle] Previous: ${this.splitEnabled}, Switching to: ${!this
        .splitEnabled}`
    );

    const currentMode = this.selectedModes[0];
    this.paymentForm.reset();

    if (this.splitEnabled && currentMode) {
      this.selectedModes = [currentMode];
      this.paymentForm.get(currentMode)?.setValue(this.totalAmount);
      console.log(
        `[Split Enabled] Retained mode '${currentMode}' with value ₹${this.totalAmount}`
      );
    } else {
      this.selectedModes = [];
      console.log('[Split Disabled] Cleared all selected modes.');
    }
  }

  selectMode(mode: string) {
    console.log(`[Mode Selection] User clicked: ${mode}`);

    if (this.splitEnabled) {
      // Toggle selection
      if (this.isSelected(mode)) {
        this.selectedModes = this.selectedModes.filter((m) => m !== mode);
        this.paymentForm.get(mode)?.setValue(0);
        console.log(`[Mode Deselected] ${mode} reset to 0`);
      } else {
        this.selectedModes.push(mode);
        console.log(`[Mode Selected] ${mode} added to split`);
      }
    } else {
      this.selectedModes = [mode];
      this.paymentForm.reset();
      this.paymentForm.get(mode)?.setValue(Math.round(this.totalAmount));
      console.log(
        `[Single Mode Selected] Mode '${mode}' set to totalAmount = ₹${this.totalAmount}`
      );
    }
  }

  // confirm() {
  //   const { cash, card, upi } = this.paymentForm.value;
  //   const payments = [];

  //   if (this.selectedModes.includes('cash') && cash > 0)
  //     payments.push({ mode: 'CASH', amount: cash });
  //   if (this.selectedModes.includes('card') && card > 0)
  //     payments.push({ mode: 'CARD', amount: card });
  //   if (this.selectedModes.includes('upi') && upi > 0)
  //     payments.push({ mode: 'UPI', amount: upi });

  //   const total = payments.reduce((sum, p) => sum + p.amount, 0);

  //   console.log('[Confirm Payment] Selected Payments:', payments);
  //   console.log(
  //     `[Confirm Payment] Total Entered = ₹${total}, Expected = ₹${this.totalAmount}`
  //   );

  //   if (total !== this.totalAmount) {
  //     console.error(
  //       `[Payment Error] Entered total ₹${total} does not match expected ₹${this.totalAmount}`
  //     );
  //     alert(`Entered total ₹${total} must equal ₹${this.totalAmount}`);
  //     return;
  //   }

  //   console.log('[Payment Success] Closing modal with payments:', payments);
  //   this.activeModal.close(payments);
  // }
  // ✅ PaymentModalComponent
  confirm() {
    const { cash, card, upi } = this.paymentForm.value;
    const payments: { mode: 'CASH' | 'CARD' | 'UPI'; amount: number }[] = [];

    if (this.selectedModes.includes('cash') && cash > 0)
      payments.push({ mode: 'CASH', amount: Math.round(cash) });
    if (this.selectedModes.includes('card') && card > 0)
      payments.push({ mode: 'CARD', amount: Math.round(card) });
    if (this.selectedModes.includes('upi') && upi > 0)
      payments.push({ mode: 'UPI', amount: Math.round(upi) });

    const total = payments.reduce((s, p) => s + p.amount, 0);
    const expected = Math.round(this.totalAmount);

    if (total !== expected) {
      alert(`Entered total ₹${total} must equal ₹${expected}`);
      return;
    }

    // ✅ Send ONLY the array
    this.activeModal.close(payments);
  }
}
